import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import api, {
  getOfferLetterTemplates,
  createOfferLetterTemplate,
  updateOfferLetterTemplate,
  deleteOfferLetterTemplate,
  seedDefaultTemplates,
  previewOfferEmailTemplate,
  sendTestOfferEmail,
  previewTemplateLegalClausesPdf
} from '../api';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Plus, FileText, Edit2, Trash2, Star, RefreshCw, Briefcase, Users, Building2, Settings2, Check, Upload, File, X, Loader2, ChevronUp, ChevronDown, ClipboardList, Paperclip, Mail, Eye, History, Zap, Scale, AlertCircle, AlertTriangle, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import TemplateClauseEditor from '../components/TemplateClauseEditor';
import TemplateAnnexuresManager from '../components/TemplateAnnexuresManager';
import EmailVersionHistoryDialog from '../components/EmailVersionHistoryDialog';
import AnnexureVersionHistoryDialog from '../components/AnnexureVersionHistoryDialog';
import AttachHgipBulkDialog from '../components/AttachHgipBulkDialog';
import NoticePeriodAuditDialog from '../components/NoticePeriodAuditDialog';
import NoticeContradictionAuditDialog from '../components/NoticeContradictionAuditDialog';
import DuplicateTemplateDialog from '../components/DuplicateTemplateDialog';

const templateTypeOptions = [
  { value: 'job_level', label: 'Job Level', icon: Briefcase },
  { value: 'department', label: 'Department', icon: Building2 },
  { value: 'employment_type', label: 'Employment Type', icon: Users },
  { value: 'custom', label: 'Custom', icon: Settings2 }
];

const templateTypeColors = {
  job_level: 'bg-blue-100 text-blue-700',
  department: 'bg-green-100 text-green-700',
  employment_type: 'bg-purple-100 text-purple-700',
  custom: 'bg-slate-100 text-slate-700'
};

/**
 * RenderPreviewPanel
 * Read-only inline preview of what WILL/WON'T appear in the generated offer
 * letter PDF, derived from the current form state. Updates live as the user
 * toggles checkboxes — no backend call needed.
 *
 * Logic mirrors the backend's `generate_offer_letter_pdf`:
 *   - Dress Code / Attendance eligibility uses TEMPLATE-NAME rules.
 *     Designation-level eligibility (Team Lead / Digital Consultant / ADSC)
 *     can only be evaluated when an offer is created, so we show the
 *     conditional message "when offer designation is eligible" if the
 *     template-name rule alone allows it.
 *   - Legal Clauses block is always 9 clauses (fixed at PDF gen).
 *   - Custom PDF terms count comes from `formData.pdf_terms_list.length`.
 */
const _isGonukkadFamily = (name) => (name || '').startsWith('GoNukkad Sales');
const _isAdscTemplate = (name) => name === 'ADSC - Work Assignment';
const _isDcInternTemplate = (name) => [
  'DC Intern - MBA Fresher',
  'DC Intern - Graduate Fresher',
  'GoNukkad Sales - DC Intern',
].includes(name);

const _previewDressCodeAttendance = (templateName) => {
  if (_isAdscTemplate(templateName)) {
    return { eligible: false, reason: 'ADSC template — globally excluded' };
  }
  if (_isGonukkadFamily(templateName)) {
    return { eligible: true, reason: 'GoNukkad family template' };
  }
  if (_isDcInternTemplate(templateName)) {
    return { eligible: true, reason: 'DC Intern template' };
  }
  // For other templates, rendering depends entirely on the OFFER's
  // designation (Team Lead / Digital Consultant / ADSC) — which the
  // template editor can't know about. Flag that to the user.
  return { eligible: 'conditional', reason: 'depends on offer designation (Team Lead / Digital Consultant)' };
};

// Defined OUTSIDE the parent to keep React's reconciler happy (otherwise it
// destroys + re-creates the subtree on every keystroke in the parent form).
const PreviewRow = ({ status, label, hint, testid }) => (
  <li className="flex items-start gap-2 text-[12px] leading-tight" data-testid={testid}>
    <span
      className={
        'inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] flex-shrink-0 mt-0.5 ' +
        (status === 'on'
          ? 'bg-emerald-100 text-emerald-700'
          : status === 'off'
            ? 'bg-rose-100 text-rose-700'
            : 'bg-amber-100 text-amber-700')
      }
    >
      {status === 'on' ? '✓' : status === 'off' ? '✗' : '~'}
    </span>
    <span className="flex-1">
      <span className={status === 'off' ? 'line-through text-slate-400' : 'text-slate-700'}>{label}</span>
      {hint && <span className="text-slate-400 ml-1">— {hint}</span>}
    </span>
  </li>
);

const RenderPreviewPanel = ({ formData }) => {
  const name = formData?.name || '';
  const baseEligibility = _previewDressCodeAttendance(name);
  const customPdfTermsCount = (formData?.pdf_terms_list || []).length;

  // Dress Code Policy row
  let dressStatus = 'on';
  let dressHint = '';
  if (formData?.hide_dress_code_policy) {
    dressStatus = 'off';
    dressHint = 'hidden via flag on this template';
  } else if (baseEligibility.eligible === false) {
    dressStatus = 'off';
    dressHint = baseEligibility.reason;
  } else if (baseEligibility.eligible === 'conditional') {
    dressStatus = 'conditional';
    dressHint = baseEligibility.reason;
  } else {
    dressHint = `will render (${baseEligibility.reason})`;
  }

  // Attendance Policy / APC row
  let attStatus = 'on';
  let attHint = '';
  let attLabel = 'Attendance Policy / APC';
  if (_isGonukkadFamily(name)) attLabel = 'Attendance & Performance Compliance';
  if (formData?.hide_attendance_policy) {
    attStatus = 'off';
    attHint = 'hidden via flag on this template';
  } else if (baseEligibility.eligible === false) {
    attStatus = 'off';
    attHint = baseEligibility.reason;
  } else if (baseEligibility.eligible === 'conditional') {
    attStatus = 'conditional';
    attHint = baseEligibility.reason;
  } else {
    attHint = `will render (${baseEligibility.reason})`;
  }

  return (
    <div className="rounded-md bg-white border border-slate-200 p-3" data-testid="render-preview-panel">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">
        PDF Render Preview
      </p>
      <ul className="space-y-1.5">
        <PreviewRow status="on" label="Compensation Structure table" hint="from salary structure settings" />
        <PreviewRow
          status="on"
          label={`${customPdfTermsCount} custom PDF Term${customPdfTermsCount === 1 ? '' : 's'}`}
          hint={customPdfTermsCount === 0 ? 'none configured yet — add in the &ldquo;PDF Terms&rdquo; tab' : 'from "PDF Terms" tab'}
        />
        <PreviewRow status={dressStatus} label="Dress Code Policy" hint={dressHint} testid="preview-row-dress-code" />
        <PreviewRow status={attStatus} label={attLabel} hint={attHint} testid="preview-row-attendance" />
        <PreviewRow
          status="on"
          label="9 Standard Legal Clauses"
          hint="Confidentiality &rarr; Non-Compete &rarr; Non-Solicit &rarr; IP &rarr; Arbitration &rarr; Gov Law &rarr; Termination &amp; Recovery &rarr; Severability &rarr; Survival"
        />
      </ul>
      <p className="text-[10px] text-slate-400 mt-2">
        <span className="text-amber-600 font-medium">~</span> = depends on the offer&apos;s designation (resolved at offer creation, not template edit).
      </p>
    </div>
  );
};


const OfferLetterTemplates = ({ user, setUser }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hgipDialogOpen, setHgipDialogOpen] = useState(false);
  const [annexureHistoryOpen, setAnnexureHistoryOpen] = useState(false);
  const [noticeAuditOpen, setNoticeAuditOpen] = useState(false);
  const [contradictionAuditOpen, setContradictionAuditOpen] = useState(false);
  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [clauseEditorOpen, setClauseEditorOpen] = useState(false);
  const [clauseEditTarget, setClauseEditTarget] = useState(null);
  const [annexuresOpen, setAnnexuresOpen] = useState(false);
  const [annexuresTarget, setAnnexuresTarget] = useState(null);
  const [legalPreviewingId, setLegalPreviewingId] = useState(null);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfParsing, setPdfParsing] = useState(false);
  const [pdfParsed, setPdfParsed] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: 'custom',
    category: '',
    is_default: false,
    salary_structure: {
      basic_da_percentage: 50,
      hra_percentage: 25,
      statutory_bonus_percentage: 5,
      other_allowance_percentage: 20,
      variable_percentage: 10,
      custom_components: []
    },
    terms_and_conditions: {
      probation_period: '6 months',
      notice_period: '30 days',
      working_hours: '9:00 AM to 6:00 PM, Monday to Saturday',
      leave_policy: 'As per company policy',
      confidentiality_clause: 'Standard confidentiality agreement applies',
      non_compete_clause: '12 months post-employment within same industry',
      additional_terms: []
    },
    pdf_terms_list: [],
    letter_of_engagement_terms: [],
    email_subject: '',
    email_body: '',
    esign_signature_position: { preset: 'auto', page: 'last' },
    // Compensation Tiers (used by DC Intern templates only). Optional —
    // regular templates leave this null so the tier editor stays hidden.
    compensation_tiers: null,
    // Per-template visibility opt-outs for the rendered policy sections.
    // Default false (existing templates render unchanged).
    hide_dress_code_policy: false,
    hide_attendance_policy: false
  });
  const [newAdditionalTerm, setNewAdditionalTerm] = useState('');
  const [newPdfTerm, setNewPdfTerm] = useState('');
  const [newLoeTerm, setNewLoeTerm] = useState('');
  const [emailPreview, setEmailPreview] = useState({ subject: '', body: '', loading: false });
  const [lastFocusedField, setLastFocusedField] = useState('body');
  const [sendingTestEmail, setSendingTestEmail] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState(user?.email || '');
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [esignPreviewImage, setEsignPreviewImage] = useState(null);  // base64 data URL or null
  const [esignPreviewLoading, setEsignPreviewLoading] = useState(false);
  const [esignPreviewMeta, setEsignPreviewMeta] = useState({ pageCount: 0, pageIndex: 0, annexureFilename: '' });

  // Handle PDF file upload and parsing
  const handlePdfUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    
    setPdfFile(file);
    setPdfParsing(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await api.post('/offer-letters/parse-pdf', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      
      const data = response.data;
      
      // Extract salary data from parsed PDF
      const parsedData = data.extracted_data || data;
        
        // Update form with extracted data
        setFormData(prev => ({
          ...prev,
          name: parsedData.designation || parsedData.position || prev.name,
          description: `Extracted from PDF: ${file.name}. ${parsedData.description || ''}`,
          salary_structure: {
            basic_da: parsedData.basic_da || parsedData.basic_salary || 0,
            hra: parsedData.hra || 0,
            statutory_bonus: parsedData.statutory_bonus || parsedData.bonus || 0,
            other_allowance: parsedData.other_allowance || parsedData.allowances || 0,
            variable_amount: parsedData.variable_amount || parsedData.variable || 0,
            gross_salary: parsedData.gross_salary || 0,
            pf_deduction: parsedData.pf_deduction || parsedData.pf || 0,
            esic_deduction: parsedData.esic_deduction || parsedData.esic || 0,
            professional_tax: parsedData.professional_tax || parsedData.pt || 200,
            total_deductions: parsedData.total_deductions || 0,
            net_salary: parsedData.net_salary || 0,
            total_ctc: parsedData.total_ctc || parsedData.ctc || 0
          },
          terms_and_conditions: {
            ...prev.terms_and_conditions,
            probation_period: parsedData.probation_period || prev.terms_and_conditions.probation_period,
            notice_period: parsedData.notice_period || prev.terms_and_conditions.notice_period,
          }
        }));
        
        setPdfParsed(true);
        toast.success('PDF parsed successfully! Please review and adjust the extracted data.');
    } catch (error) {
      console.error('Error parsing PDF:', error);
      const msg = error.response?.data?.detail || error.message || 'Error parsing PDF';
      toast.error(`${msg}. Please enter details manually.`);
    } finally {
      setPdfParsing(false);
    }
  };

  const removePdfFile = () => {
    setPdfFile(null);
    setPdfParsed(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await getOfferLetterTemplates();
      setTemplates(response.data);
    } catch (error) {
      toast.error('Failed to fetch templates');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await seedDefaultTemplates();
      toast.success(response.data.message);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed default templates');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await updateOfferLetterTemplate(editingTemplate.id, formData);
        toast.success('Template updated successfully');
      } else {
        await createOfferLetterTemplate(formData);
        toast.success('Template created successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleEdit = (template) => {
    setEditingTemplate(template);
    setFormData({
      name: template.name,
      description: template.description || '',
      template_type: template.template_type,
      category: template.category || '',
      is_default: template.is_default,
      salary_structure: template.salary_structure ? {
        basic_da_percentage: 50,
        hra_percentage: 25,
        statutory_bonus_percentage: 5,
        other_allowance_percentage: 20,
        variable_percentage: 10,
        ...template.salary_structure,
        // Legacy templates persisted without `custom_components` — normalise
        // to [] so the UI's `.map` / `.reduce` don't blow up.
        custom_components: template.salary_structure.custom_components || [],
        // Same normalisation for the TDS config (added later).
        tds_kind: template.salary_structure.tds_kind || 'fixed',
        tds_amount: template.salary_structure.tds_amount ?? 0,
        tds_percentage: template.salary_structure.tds_percentage ?? 0
      } : {
        basic_da_percentage: 50,
        hra_percentage: 25,
        statutory_bonus_percentage: 5,
        other_allowance_percentage: 20,
        variable_percentage: 10,
        custom_components: [],
        tds_kind: 'fixed',
        tds_amount: 0,
        tds_percentage: 0
      },
      terms_and_conditions: template.terms_and_conditions || {
        probation_period: '6 months',
        notice_period: '30 days',
        working_hours: '9:00 AM to 6:00 PM, Monday to Saturday',
        leave_policy: 'As per company policy',
        confidentiality_clause: 'Standard confidentiality agreement applies',
        non_compete_clause: '12 months post-employment within same industry',
        additional_terms: []
      },
      pdf_terms_list: template.pdf_terms_list || [],
      letter_of_engagement_terms: template.letter_of_engagement_terms || [],
      email_subject: template.email_subject || '',
      email_body: template.email_body || '',
      esign_signature_position: template.esign_signature_position || { preset: 'auto', page: 'last' },
      compensation_tiers: template.compensation_tiers || null,
      hide_dress_code_policy: !!template.hide_dress_code_policy,
      hide_attendance_policy: !!template.hide_attendance_policy
    });
    setDialogOpen(true);
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      await deleteOfferLetterTemplate(templateId);
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  /**
   * One-click "Preview Clauses PDF" — fetches the lightweight 8-clauses-only
   * PDF for this template and opens it in a new tab. No DB writes, no full
   * offer-letter pipeline — purely a legal-block sanity check.
   *
   * Fallback: if the browser blocks the new-tab popup we trigger a download
   * so HR isn't silently left without the file.
   */
  const handleLegalPreview = async (template) => {
    if (!template?.id) return;
    setLegalPreviewingId(template.id);
    try {
      const res = await previewTemplateLegalClausesPdf(template.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank');
      if (!w) {
        const a = document.createElement('a');
        a.href = url;
        a.download = `legal-clauses-${(template.name || 'template').replace(/[^A-Za-z0-9_-]+/g, '_')}.pdf`;
        a.click();
      }
      // Keep blob alive long enough for the new tab to load, then clean up.
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('Legal clauses preview opened in a new tab.');
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to generate legal clauses preview');
    } finally {
      setLegalPreviewingId(null);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setFormData({
      name: '',
      description: '',
      template_type: 'custom',
      category: '',
      is_default: false,
      salary_structure: {
        basic_da_percentage: 50,
        hra_percentage: 25,
        statutory_bonus_percentage: 5,
        other_allowance_percentage: 20,
        variable_percentage: 10,
        custom_components: [],
        tds_kind: 'fixed',
        tds_amount: 0,
        tds_percentage: 0
      },
      terms_and_conditions: {
        probation_period: '6 months',
        notice_period: '30 days',
        working_hours: '9:00 AM to 6:00 PM, Monday to Saturday',
        leave_policy: 'As per company policy',
        confidentiality_clause: 'Standard confidentiality agreement applies',
        non_compete_clause: '12 months post-employment within same industry',
        additional_terms: []
      },
      pdf_terms_list: [],
      letter_of_engagement_terms: [],
      email_subject: '',
      email_body: '',
      esign_signature_position: { preset: 'auto', page: 'last' },
      compensation_tiers: null,
      hide_dress_code_policy: false,
      hide_attendance_policy: false
    });
    setNewAdditionalTerm('');
    setNewPdfTerm('');
    setEmailPreview({ subject: '', body: '', loading: false });
  };

  const addAdditionalTerm = () => {
    if (newAdditionalTerm.trim()) {
      setFormData({
        ...formData,
        terms_and_conditions: {
          ...formData.terms_and_conditions,
          additional_terms: [...formData.terms_and_conditions.additional_terms, newAdditionalTerm.trim()]
        }
      });
      setNewAdditionalTerm('');
    }
  };

  const removeAdditionalTerm = (index) => {
    setFormData({
      ...formData,
      terms_and_conditions: {
        ...formData.terms_and_conditions,
        additional_terms: formData.terms_and_conditions.additional_terms.filter((_, i) => i !== index)
      }
    });
  };

  const addPdfTerm = () => {
    if (newPdfTerm.trim()) {
      setFormData({ ...formData, pdf_terms_list: [...(formData.pdf_terms_list || []), newPdfTerm.trim()] });
      setNewPdfTerm('');
    }
  };

  const removePdfTerm = (index) => {
    setFormData({ ...formData, pdf_terms_list: (formData.pdf_terms_list || []).filter((_, i) => i !== index) });
  };

  const movePdfTerm = (index, direction) => {
    const terms = [...(formData.pdf_terms_list || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= terms.length) return;
    [terms[index], terms[newIndex]] = [terms[newIndex], terms[index]];
    setFormData({ ...formData, pdf_terms_list: terms });
  };

  // ─── Letter of Engagement (page 3) term handlers ────────────────────
  // These 20 clauses were previously hardcoded in the backend PDF
  // generator. HR can now override them per-template. Same UX as the
  // PDF Terms tab — add/remove/reorder, placeholders like
  // {candidate_name}, {company_name}, {joining_days} auto-substitute.
  const addLoeTerm = () => {
    if (newLoeTerm.trim()) {
      setFormData({
        ...formData,
        letter_of_engagement_terms: [
          ...(formData.letter_of_engagement_terms || []),
          newLoeTerm.trim(),
        ],
      });
      setNewLoeTerm('');
    }
  };
  const removeLoeTerm = (index) => {
    setFormData({
      ...formData,
      letter_of_engagement_terms: (formData.letter_of_engagement_terms || []).filter((_, i) => i !== index),
    });
  };
  const moveLoeTerm = (index, direction) => {
    const terms = [...(formData.letter_of_engagement_terms || [])];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= terms.length) return;
    [terms[index], terms[newIndex]] = [terms[newIndex], terms[index]];
    setFormData({ ...formData, letter_of_engagement_terms: terms });
  };
  const updateLoeTerm = (index, value) => {
    const terms = [...(formData.letter_of_engagement_terms || [])];
    terms[index] = value;
    setFormData({ ...formData, letter_of_engagement_terms: terms });
  };


  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Offer Letter Templates</h1>
            <p className="text-slate-600 mt-1">Manage templates for different offer letter types</p>
          </div>
          <div className="flex gap-2">
            {templates.length === 0 && (
              <Button variant="outline" onClick={handleSeedDefaults} className="rounded-full">
                <RefreshCw className="mr-2" size={18} />
                Load Default Templates
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setNoticeAuditOpen(true)}
              className="rounded-full border-amber-300 text-amber-700 hover:bg-amber-50"
              title="Find templates whose notice_period contradicts the new tiered policy"
              data-testid="notice-period-audit-btn"
            >
              <AlertCircle className="mr-2" size={16} />
              Notice Audit
            </Button>
            <Button
              variant="outline"
              onClick={() => setContradictionAuditOpen(true)}
              className="rounded-full border-red-300 text-red-700 hover:bg-red-50"
              title="Scan every template's terms + clauses for content that contradicts the new tiered Termination & Recovery clause"
              data-testid="notice-contradiction-audit-btn"
            >
              <AlertTriangle className="mr-2" size={16} />
              Contradiction Audit
            </Button>
            <Button
              variant="outline"
              onClick={() => setHgipDialogOpen(true)}
              className="rounded-full border-cyan-300 text-cyan-700 hover:bg-cyan-50"
              data-testid="attach-hgip-bulk-btn"
            >
              <Zap className="mr-2" size={16} />
              Attach HGIP to Sales Roles
            </Button>
            <Button
              variant="outline"
              onClick={() => setAnnexureHistoryOpen(true)}
              className="rounded-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              title="View / publish / rollback Salary Annexure A versions (DC & TL)"
              data-testid="annexure-version-history-btn"
            >
              <FileText className="mr-2" size={16} />
              Annexure History
            </Button>
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">
                  <Plus className="mr-2" size={18} />
                  Create Template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingTemplate ? 'Edit Template' : 'Create New Template'}</DialogTitle>
                </DialogHeader>
                
                {/* PDF Upload Section - Only for new templates */}
                {!editingTemplate && (
                  <div className="mb-4 p-4 border-2 border-dashed rounded-lg bg-slate-50">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="font-semibold text-slate-700">Upload Offer Letter PDF (Optional)</Label>
                      {pdfFile && (
                        <Button variant="ghost" size="sm" onClick={removePdfFile} className="text-red-500 hover:text-red-700">
                          <X size={16} className="mr-1" /> Remove
                        </Button>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mb-3">
                      Upload an existing offer letter PDF to automatically extract salary structure and terms
                    </p>
                    
                    {!pdfFile ? (
                      <div 
                        className="flex flex-col items-center justify-center p-6 border rounded-lg bg-white cursor-pointer hover:bg-slate-50 transition-colors"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload className="text-slate-400 mb-2" size={32} />
                        <p className="text-sm text-slate-600">Click to upload PDF</p>
                        <p className="text-xs text-slate-400">or drag and drop</p>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          onChange={handlePdfUpload}
                          className="hidden"
                        />
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 p-3 border rounded-lg bg-white">
                        <File className="text-red-500" size={24} />
                        <div className="flex-1">
                          <p className="font-medium text-sm">{pdfFile.name}</p>
                          <p className="text-xs text-slate-500">
                            {pdfParsing ? 'Parsing...' : pdfParsed ? '✓ Parsed successfully' : 'Ready to parse'}
                          </p>
                        </div>
                        {pdfParsing && <Loader2 className="animate-spin text-indigo-600" size={20} />}
                        {pdfParsed && <Check className="text-green-600" size={20} />}
                      </div>
                    )}
                  </div>
                )}
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <Tabs defaultValue="basic" className="w-full">
                    <TabsList className="grid w-full grid-cols-6">
                      <TabsTrigger value="basic">Basic Info</TabsTrigger>
                      <TabsTrigger value="salary">Salary Structure</TabsTrigger>
                      <TabsTrigger value="terms">Terms & Conditions</TabsTrigger>
                      <TabsTrigger value="pdfterms">PDF Terms</TabsTrigger>
                      <TabsTrigger value="loeterms" data-testid="loe-terms-tab">Letter of Engagement</TabsTrigger>
                      <TabsTrigger value="email" data-testid="tab-email-template">Email Template</TabsTrigger>
                      <TabsTrigger value="esign" data-testid="tab-esign">E-Sign</TabsTrigger>
                    </TabsList>

                    <TabsContent value="basic" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="name">Template Name *</Label>
                          <Input
                            id="name"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="e.g., Senior Engineer Template"
                            required
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="template_type">Template Type *</Label>
                          <Select
                            value={formData.template_type}
                            onValueChange={(value) => setFormData({ ...formData, template_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select type" />
                            </SelectTrigger>
                            <SelectContent>
                              {templateTypeOptions.map(option => (
                                <SelectItem key={option.value} value={option.value}>
                                  {option.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="category">Category</Label>
                          <Input
                            id="category"
                            value={formData.category}
                            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                            placeholder="e.g., Junior, Senior, IT, Contract"
                          />
                        </div>
                        <div className="space-y-2 flex items-end">
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              id="is_default"
                              checked={formData.is_default}
                              onCheckedChange={(checked) => setFormData({ ...formData, is_default: checked })}
                            />
                            <Label htmlFor="is_default" className="cursor-pointer">Set as Default Template</Label>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Brief description of when to use this template"
                          rows={3}
                        />
                      </div>

                      {/* Per-template policy visibility toggles ------------------ */}
                      {/* Hides specific policy sections in the generated PDF without */}
                      {/* changing global eligibility rules. Useful for variant       */}
                      {/* templates (e.g. GoNukkad Team Lead) that share the parent's */}
                      {/* structure but omit certain sections.                        */}
                      <div className="rounded-lg border border-slate-200 bg-slate-50/50 p-3 space-y-3">
                        <div>
                          <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Policy Section Visibility
                          </Label>
                          <p className="text-[11px] text-slate-400 mt-0.5">
                            Hide specific policy sections in the generated offer letter PDF. Only affects this template.
                          </p>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hide_dress_code_policy"
                            checked={!!formData.hide_dress_code_policy}
                            onCheckedChange={(checked) => setFormData({ ...formData, hide_dress_code_policy: !!checked })}
                            data-testid="hide-dress-code-policy-checkbox"
                          />
                          <Label htmlFor="hide_dress_code_policy" className="cursor-pointer text-sm">
                            Hide <span className="font-medium">Dress Code Policy</span> section
                          </Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="hide_attendance_policy"
                            checked={!!formData.hide_attendance_policy}
                            onCheckedChange={(checked) => setFormData({ ...formData, hide_attendance_policy: !!checked })}
                            data-testid="hide-attendance-policy-checkbox"
                          />
                          <Label htmlFor="hide_attendance_policy" className="cursor-pointer text-sm">
                            Hide <span className="font-medium">Attendance Policy</span> /{' '}
                            <span className="font-medium">Attendance &amp; Performance Compliance</span> section
                          </Label>
                        </div>

                        {/* ─── Render Preview ──────────────────────────────────── */}
                        {/* Live read-only sanity-check of what the generated PDF  */}
                        {/* will / won't contain. Updates instantly as HR toggles  */}
                        {/* the checkboxes above. No backend call — purely a       */}
                        {/* client-side derivation of the same rules the renderer  */}
                        {/* applies. Useful so HR doesn't need to generate a test  */}
                        {/* PDF just to verify which sections will appear.         */}
                        <RenderPreviewPanel formData={formData} />
                      </div>
                    </TabsContent>

                    <TabsContent value="salary" className="space-y-4 mt-4">
                      {pdfParsed ? (
                        <>
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-4">
                            <p className="text-sm text-green-700">
                              ✓ Salary structure extracted from PDF. Review and adjust values as needed.
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Basic + DA (Monthly)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.basic_da || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, basic_da: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 15000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>HRA (Monthly)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.hra || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, hra: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 7500"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Statutory Bonus (Monthly)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.statutory_bonus || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, statutory_bonus: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 1500"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Other Allowance (Monthly)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.other_allowance || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, other_allowance: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 6000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Variable Amount (Annual)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.variable_amount || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, variable_amount: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 100000"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Professional Tax (Monthly)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.professional_tax || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, professional_tax: parseFloat(e.target.value) || 0 }
                                })}
                                placeholder="e.g., 200"
                              />
                            </div>
                          </div>
                          <Card className="bg-slate-50 border-slate-200 mt-4">
                            <CardContent className="p-4">
                              <p className="text-sm font-medium text-slate-700 mb-2">Calculated Values:</p>
                              <div className="grid grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="text-slate-500">Gross:</span> Rs. {((formData.salary_structure.basic_da || 0) + (formData.salary_structure.hra || 0) + (formData.salary_structure.statutory_bonus || 0) + (formData.salary_structure.other_allowance || 0)).toLocaleString()}/month
                                </div>
                                <div>
                                  <span className="text-slate-500">Net:</span> Rs. {((formData.salary_structure.basic_da || 0) + (formData.salary_structure.hra || 0) + (formData.salary_structure.statutory_bonus || 0) + (formData.salary_structure.other_allowance || 0) - (formData.salary_structure.professional_tax || 0)).toLocaleString()}/month
                                </div>
                                <div>
                                  <span className="text-slate-500">CTC:</span> Rs. {(((formData.salary_structure.basic_da || 0) + (formData.salary_structure.hra || 0) + (formData.salary_structure.statutory_bonus || 0) + (formData.salary_structure.other_allowance || 0)) * 12 + (formData.salary_structure.variable_amount || 0)).toLocaleString()}/year
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </>
                      ) : (
                        <>
                          <p className="text-sm text-slate-500 mb-4">
                            Define default salary structure percentages. These serve as guidelines when creating offers.
                            <br /><span className="text-indigo-600">Tip: Upload a PDF above to extract actual salary values automatically.</span>
                          </p>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Basic + DA (%)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.basic_da_percentage}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, basic_da_percentage: parseFloat(e.target.value) || 0 }
                                })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>HRA (%)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.hra_percentage}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, hra_percentage: parseFloat(e.target.value) || 0 }
                                })}
                              />
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Statutory Bonus (%)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.statutory_bonus_percentage}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, statutory_bonus_percentage: parseFloat(e.target.value) || 0 }
                                })}
                              />
                            </div>
                            <div className="space-y-2">
                              <Label>Other Allowance (%)</Label>
                              <Input
                                type="number"
                                value={formData.salary_structure.other_allowance_percentage}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, other_allowance_percentage: parseFloat(e.target.value) || 0 }
                                })}
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Variable (% of Annual CTC)</Label>
                            <Input
                              type="number"
                              value={formData.salary_structure.variable_percentage}
                              onChange={(e) => setFormData({
                                ...formData,
                                salary_structure: { ...formData.salary_structure, variable_percentage: parseFloat(e.target.value) || 0 }
                              })}
                            />
                          </div>

                          {/* ─── TDS Deduction default ─────────────────────
                              Pre-fills the "TDS (Income Tax)" row on every
                              offer created from this template. HR can still
                              override the amount per offer.
                          */}
                          <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="tds-config-section">
                            <Label>TDS Deduction (Income Tax) — default</Label>
                            <div className="grid grid-cols-2 gap-4">
                              <select
                                value={formData.salary_structure.tds_kind || 'fixed'}
                                className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid="tds-kind-select"
                                onChange={(e) => setFormData({
                                  ...formData,
                                  salary_structure: { ...formData.salary_structure, tds_kind: e.target.value }
                                })}
                              >
                                <option value="fixed">Fixed ₹/month</option>
                                <option value="percentage">% of gross</option>
                              </select>
                              <Input
                                type="number"
                                placeholder={(formData.salary_structure.tds_kind || 'fixed') === 'fixed' ? '₹ per month' : '% of gross'}
                                value={(formData.salary_structure.tds_kind || 'fixed') === 'fixed'
                                  ? formData.salary_structure.tds_amount
                                  : formData.salary_structure.tds_percentage}
                                data-testid="tds-value-input"
                                onChange={(e) => {
                                  const v = parseFloat(e.target.value) || 0;
                                  const key = (formData.salary_structure.tds_kind || 'fixed') === 'fixed' ? 'tds_amount' : 'tds_percentage';
                                  setFormData({
                                    ...formData,
                                    salary_structure: { ...formData.salary_structure, [key]: v }
                                  });
                                }}
                              />
                            </div>
                            <p className="text-[11px] text-slate-500">
                              0 = no TDS row pre-filled. New offers from this template inherit this value; HR can adjust it per offer.
                            </p>
                          </div>

                          {/* ─── Custom Earning Components (Feb 2026) ─────────
                              Optional additional earning rows. They render
                              between "Other Allowance" and "Gross Salary" in
                              the generated OL PDF and count toward the same
                              100% total constraint as the built-in rows.
                          */}
                          <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="custom-components-section">
                            <div className="flex items-center justify-between">
                              <Label>Custom Earning Components</Label>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs"
                                data-testid="add-custom-component-btn"
                                onClick={() => {
                                  const existing = formData.salary_structure.custom_components || [];
                                  const slug = `component_${existing.length + 1}`;
                                  setFormData({
                                    ...formData,
                                    salary_structure: {
                                      ...formData.salary_structure,
                                      custom_components: [...existing, { key: slug, label: '', percentage: 0 }],
                                    },
                                  });
                                }}
                              >
                                + Add Component
                              </Button>
                            </div>
                            {(formData.salary_structure.custom_components || []).length === 0 ? (
                              <p className="text-[11px] text-slate-500">
                                No custom components yet — click <b>+ Add Component</b> to insert extra earning rows (e.g. Transport Allowance, Meal Card). Choose <b>% of gross</b> for a percentage split (counts toward the 100% total), or <b>Fixed ₹/mo</b> for a flat monthly amount (does not count toward the 100% — added on top).
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {(formData.salary_structure.custom_components || []).map((comp, idx) => {
                                  const kind = comp.kind || 'percentage';
                                  return (
                                    <div
                                      key={idx}
                                      className="grid grid-cols-[1fr_110px_100px_36px] gap-2 items-center"
                                      data-testid={`custom-component-row-${idx}`}
                                    >
                                      <Input
                                        placeholder="Component name (e.g. Conveyance)"
                                        value={comp.label || ''}
                                        data-testid={`custom-component-label-${idx}`}
                                        onChange={(e) => {
                                          const next = [...(formData.salary_structure.custom_components || [])];
                                          next[idx] = { ...next[idx], label: e.target.value };
                                          setFormData({
                                            ...formData,
                                            salary_structure: { ...formData.salary_structure, custom_components: next },
                                          });
                                        }}
                                      />
                                      <select
                                        value={kind}
                                        className="h-9 rounded-md border border-slate-200 bg-white px-2 text-xs"
                                        data-testid={`custom-component-kind-${idx}`}
                                        onChange={(e) => {
                                          const next = [...(formData.salary_structure.custom_components || [])];
                                          next[idx] = { ...next[idx], kind: e.target.value };
                                          setFormData({
                                            ...formData,
                                            salary_structure: { ...formData.salary_structure, custom_components: next },
                                          });
                                        }}
                                      >
                                        <option value="percentage">% of gross</option>
                                        <option value="fixed">Fixed ₹/mo</option>
                                      </select>
                                      <Input
                                        type="number"
                                        placeholder={kind === 'fixed' ? '₹/month' : '%'}
                                        value={kind === 'fixed' ? (comp.amount ?? '') : (comp.percentage ?? '')}
                                        data-testid={`custom-component-value-${idx}`}
                                        onChange={(e) => {
                                          const num = parseFloat(e.target.value) || 0;
                                          const next = [...(formData.salary_structure.custom_components || [])];
                                          next[idx] = kind === 'fixed'
                                            ? { ...next[idx], amount: num }
                                            : { ...next[idx], percentage: num };
                                          setFormData({
                                            ...formData,
                                            salary_structure: { ...formData.salary_structure, custom_components: next },
                                          });
                                        }}
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-9 w-9 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                        data-testid={`remove-custom-component-${idx}`}
                                        onClick={() => {
                                          const next = (formData.salary_structure.custom_components || []).filter((_, i) => i !== idx);
                                          setFormData({
                                            ...formData,
                                            salary_structure: { ...formData.salary_structure, custom_components: next },
                                          });
                                        }}
                                        aria-label="Remove component"
                                      >
                                        ×
                                      </Button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          <Card className="bg-slate-50 border-slate-200">
                            <CardContent className="p-4">
                              <p className="text-sm text-slate-600">
                                <strong>Total:</strong> {
                                  (formData.salary_structure.basic_da_percentage || 0) +
                                  (formData.salary_structure.hra_percentage || 0) +
                                  (formData.salary_structure.statutory_bonus_percentage || 0) +
                                  (formData.salary_structure.other_allowance_percentage || 0) +
                                  (formData.salary_structure.custom_components || [])
                                    .filter((c) => (c.kind || 'percentage') === 'percentage')
                                    .reduce((s, c) => s + (parseFloat(c.percentage) || 0), 0)
                                }% (should equal 100%)
                              </p>
                            </CardContent>
                          </Card>
                        </>
                      )}

                      {/* ─── Compensation Tiers (DC Intern templates) ─────────────────
                          Two-tier compensation editor. Enabled only when the template
                          already has `compensation_tiers` on it (e.g. any of the
                          "DC Intern - …" seed templates) OR when HR explicitly
                          toggles it on via the "Enable Tiered Compensation" button
                          below. Emits the same shape the backend + PDF renderer
                          consume:
                            {
                              first_3_months: { label, fixed_annual, variable_annual, ctc_annual, monthly_gross },
                              from_month_4:   { label, fixed_annual, variable_annual, ctc_annual, monthly_gross },
                            }
                          CTC is auto-derived from Fixed + Variable so HR only edits
                          the two source numbers. Monthly Gross is set to ctc/12
                          rounded down to a whole rupee.
                      */}
                      {formData.compensation_tiers ? (
                        <div
                          className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-4 space-y-3 mt-4"
                          data-testid="compensation-tiers-editor"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <Label className="text-xs font-semibold uppercase tracking-wide text-indigo-800">
                                Compensation Tiers (DC Intern)
                              </Label>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Two-phase compensation (e.g. OJT + Post-OJT). HR can rename the phases and edit the numbers. Reflected on both Page 1 (summary table) and Page 5 (Annexure) of the offer PDF.
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="text-xs text-red-600 hover:bg-red-50 h-7"
                              onClick={() => setFormData({ ...formData, compensation_tiers: null })}
                              data-testid="disable-comp-tiers-btn"
                            >
                              Disable
                            </Button>
                          </div>

                          {['first_3_months', 'from_month_4'].map((tierKey, tIdx) => {
                            const tier = formData.compensation_tiers?.[tierKey] || {};
                            const fixed = Number(tier.fixed_annual || 0);
                            const variable = Number(tier.variable_annual || 0);
                            const ctc = fixed + variable;
                            const updateTier = (patch) => {
                              const newTier = { ...tier, ...patch };
                              // Auto-derive ctc + monthly_gross whenever fixed/variable change
                              const newFixed = Number(newTier.fixed_annual || 0);
                              const newVariable = Number(newTier.variable_annual || 0);
                              newTier.ctc_annual = newFixed + newVariable;
                              newTier.monthly_gross = Math.floor(newFixed / 12);
                              setFormData({
                                ...formData,
                                compensation_tiers: { ...formData.compensation_tiers, [tierKey]: newTier },
                              });
                            };
                            return (
                              <div
                                key={tierKey}
                                className="rounded border border-slate-200 bg-white p-3 space-y-2"
                                data-testid={`comp-tier-row-${tIdx}`}
                              >
                                <div className="grid grid-cols-4 gap-2 items-end">
                                  <div className="space-y-1 col-span-1">
                                    <Label className="text-[11px] text-slate-600">Phase Label</Label>
                                    <Input
                                      className="h-8 text-xs"
                                      value={tier.label || ''}
                                      onChange={(e) => updateTier({ label: e.target.value })}
                                      placeholder={tIdx === 0 ? 'OJT (1 Month)' : 'Post-OJT'}
                                      data-testid={`comp-tier-label-${tIdx}`}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-slate-600">Fixed (Annual Rs.)</Label>
                                    <Input
                                      type="number"
                                      className="h-8 text-xs"
                                      value={tier.fixed_annual ?? ''}
                                      onChange={(e) => updateTier({ fixed_annual: parseInt(e.target.value, 10) || 0 })}
                                      placeholder={tIdx === 0 ? '180000' : '300000'}
                                      data-testid={`comp-tier-fixed-${tIdx}`}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-slate-600">Variable (Annual Rs.)</Label>
                                    <Input
                                      type="number"
                                      className="h-8 text-xs"
                                      value={tier.variable_annual ?? ''}
                                      onChange={(e) => updateTier({ variable_annual: parseInt(e.target.value, 10) || 0 })}
                                      placeholder="120000"
                                      data-testid={`comp-tier-variable-${tIdx}`}
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <Label className="text-[11px] text-slate-600">CTC (Auto)</Label>
                                    <div
                                      className="h-8 text-xs px-2 py-1.5 rounded border border-slate-200 bg-slate-50 text-slate-700 font-medium"
                                      data-testid={`comp-tier-ctc-${tIdx}`}
                                    >
                                      Rs. {ctc.toLocaleString('en-IN')}
                                    </div>
                                  </div>
                                </div>
                                <p className="text-[10px] text-slate-400">
                                  Monthly Gross: Rs. {Math.floor(fixed / 12).toLocaleString('en-IN')} (auto-derived)
                                </p>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-4">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="text-xs border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                            onClick={() => setFormData({
                              ...formData,
                              compensation_tiers: {
                                first_3_months: {
                                  label: 'OJT (1 Month)',
                                  fixed_annual: 180000,
                                  variable_annual: 120000,
                                  ctc_annual: 300000,
                                  monthly_gross: 15000,
                                },
                                from_month_4: {
                                  label: 'Post-OJT',
                                  fixed_annual: 300000,
                                  variable_annual: 120000,
                                  ctc_annual: 420000,
                                  monthly_gross: 25000,
                                },
                              },
                            })}
                            data-testid="enable-comp-tiers-btn"
                          >
                            + Enable Tiered Compensation (DC Intern)
                          </Button>
                          <p className="text-[11px] text-slate-400 mt-1">
                            For DC Intern-style templates that split compensation into two phases (e.g. OJT + Post-OJT).
                          </p>
                        </div>
                      )}
                    </TabsContent>

                    <TabsContent value="terms" className="space-y-4 mt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Probation Period</Label>
                          <Input
                            value={formData.terms_and_conditions.probation_period}
                            onChange={(e) => setFormData({
                              ...formData,
                              terms_and_conditions: { ...formData.terms_and_conditions, probation_period: e.target.value }
                            })}
                            placeholder="e.g., 6 months"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Notice Period</Label>
                          <Input
                            value={formData.terms_and_conditions.notice_period}
                            onChange={(e) => setFormData({
                              ...formData,
                              terms_and_conditions: { ...formData.terms_and_conditions, notice_period: e.target.value }
                            })}
                            placeholder="e.g., 30 days"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Working Hours</Label>
                        <Input
                          value={formData.terms_and_conditions.working_hours}
                          onChange={(e) => setFormData({
                            ...formData,
                            terms_and_conditions: { ...formData.terms_and_conditions, working_hours: e.target.value }
                          })}
                          placeholder="e.g., 9:00 AM to 6:00 PM, Monday to Saturday"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Leave Policy</Label>
                        <Input
                          value={formData.terms_and_conditions.leave_policy}
                          onChange={(e) => setFormData({
                            ...formData,
                            terms_and_conditions: { ...formData.terms_and_conditions, leave_policy: e.target.value }
                          })}
                          placeholder="e.g., As per company policy"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Confidentiality Clause</Label>
                        <Textarea
                          value={formData.terms_and_conditions.confidentiality_clause}
                          onChange={(e) => setFormData({
                            ...formData,
                            terms_and_conditions: { ...formData.terms_and_conditions, confidentiality_clause: e.target.value }
                          })}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Non-Compete Clause</Label>
                        <Textarea
                          value={formData.terms_and_conditions.non_compete_clause}
                          onChange={(e) => setFormData({
                            ...formData,
                            terms_and_conditions: { ...formData.terms_and_conditions, non_compete_clause: e.target.value }
                          })}
                          rows={2}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Additional Terms</Label>
                        <div className="flex gap-2">
                          <Input
                            value={newAdditionalTerm}
                            onChange={(e) => setNewAdditionalTerm(e.target.value)}
                            placeholder="Add custom term..."
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAdditionalTerm())}
                          />
                          <Button type="button" onClick={addAdditionalTerm} variant="outline">
                            <Plus size={16} />
                          </Button>
                        </div>
                        {(formData.terms_and_conditions?.additional_terms || []).length > 0 && (
                          <div className="space-y-2 mt-2">
                            {(formData.terms_and_conditions?.additional_terms || []).map((term, index) => (
                              <div key={index} className="flex items-center justify-between bg-slate-50 p-2 rounded">
                                <span className="text-sm">{term}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() => removeAdditionalTerm(index)}
                                >
                                  <Trash2 size={14} className="text-red-500" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="pdfterms" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div>
                          <Label>PDF Terms List</Label>
                          <p className="text-xs text-slate-500 mb-2">These numbered terms appear on page 2 of the generated PDF. Drag to reorder, edit or remove as needed.</p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={newPdfTerm}
                            onChange={(e) => setNewPdfTerm(e.target.value)}
                            placeholder="Add a new term..."
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addPdfTerm())}
                            data-testid="new-pdf-term-input"
                          />
                          <Button type="button" onClick={addPdfTerm} variant="outline" data-testid="add-pdf-term-btn">Add</Button>
                        </div>
                        {(formData.pdf_terms_list || []).length > 0 && (
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {formData.pdf_terms_list.map((term, index) => (
                              <div key={index} className="flex items-start gap-2 bg-slate-50 p-2 rounded border text-sm">
                                <span className="text-xs font-bold text-slate-400 mt-1 min-w-[20px]">{index + 1}.</span>
                                <p className="flex-1 text-slate-700 text-xs leading-relaxed">{term}</p>
                                <div className="flex flex-col gap-0.5">
                                  <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => movePdfTerm(index, -1)} disabled={index === 0}>
                                    <ChevronUp size={12} />
                                  </Button>
                                  <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => movePdfTerm(index, 1)} disabled={index === (formData.pdf_terms_list || []).length - 1}>
                                    <ChevronDown size={12} />
                                  </Button>
                                </div>
                                <Button
                                  type="button" variant="ghost" size="sm"
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => removePdfTerm(index)}
                                  data-testid={`remove-pdf-term-${index}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(formData.pdf_terms_list || []).length === 0 && (
                          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg bg-slate-50">
                            No PDF terms configured. Default terms will be used.
                            <br />
                            <Button type="button" variant="link" className="text-indigo-500 text-xs mt-1" onClick={async () => {
                              try {
                                const res = await api.post('/offer-letters/migrate-template-terms');
                                toast.success(res.data.message);
                                fetchTemplates();
                              } catch (e) { toast.error('Failed to migrate terms'); }
                            }}>
                              Click to populate default terms for all templates
                            </Button>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    {/* ─── Letter of Engagement (page 3) — Feb 2026 ───────────
                        Per-template override for the 20 numbered clauses on the
                        LOE page. Leave the list empty to use the built-in
                        defaults. Placeholders {candidate_name}, {company_name},
                        {joining_days}, etc. auto-substitute per candidate.
                    */}
                    <TabsContent value="loeterms" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div>
                          <Label>Letter of Engagement — Clauses</Label>
                          <p className="text-xs text-slate-500 mb-1">
                            Numbered clauses rendered on the &quot;Letter of Engagement&quot; page (page 3). Leave the list empty to keep the built-in 20-clause default.
                          </p>
                          <p className="text-[11px] text-slate-500">
                            <b>Placeholders</b> auto-fill per candidate:
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{candidate_name}'}</code>
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{designation}'}</code>
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{company_name}'}</code>
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{work_location}'}</code>
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{start_date}'}</code>
                            <code className="text-[10px] bg-slate-100 px-1 py-0.5 rounded ml-1">{'{joining_days}'}</code>
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={newLoeTerm}
                            onChange={(e) => setNewLoeTerm(e.target.value)}
                            placeholder="Add a new clause..."
                            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addLoeTerm())}
                            data-testid="new-loe-term-input"
                          />
                          <Button type="button" onClick={addLoeTerm} variant="outline" data-testid="add-loe-term-btn">Add</Button>
                        </div>
                        {(formData.letter_of_engagement_terms || []).length > 0 && (
                          <div className="space-y-2 max-h-[300px] overflow-y-auto">
                            {formData.letter_of_engagement_terms.map((term, index) => (
                              <div key={index} className="flex items-start gap-2 bg-slate-50 p-2 rounded border text-sm" data-testid={`loe-term-row-${index}`}>
                                <span className="text-xs font-bold text-slate-400 mt-2 min-w-[20px]">{index + 1}.</span>
                                <Input
                                  value={term}
                                  onChange={(e) => updateLoeTerm(index, e.target.value)}
                                  className="flex-1 h-8 text-xs"
                                  data-testid={`loe-term-input-${index}`}
                                />
                                <div className="flex flex-col gap-0.5">
                                  <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => moveLoeTerm(index, -1)} disabled={index === 0}>
                                    <ChevronUp size={12} />
                                  </Button>
                                  <Button type="button" variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => moveLoeTerm(index, 1)} disabled={index === (formData.letter_of_engagement_terms || []).length - 1}>
                                    <ChevronDown size={12} />
                                  </Button>
                                </div>
                                <Button
                                  type="button" variant="ghost" size="sm"
                                  className="text-red-500 hover:text-red-700 h-6 w-6 p-0"
                                  onClick={() => removeLoeTerm(index)}
                                  data-testid={`remove-loe-term-${index}`}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(formData.letter_of_engagement_terms || []).length === 0 && (
                          <div className="text-center py-6 text-slate-400 text-sm border rounded-lg bg-slate-50">
                            No override configured — the built-in 20 clauses will be used.
                            <br />
                            <Button
                              type="button"
                              variant="link"
                              className="text-indigo-500 text-xs mt-1"
                              data-testid="loe-load-defaults-btn"
                              onClick={() => {
                                // Pre-populate with the same 20 defaults the
                                // backend uses, so HR can tweak individual
                                // lines instead of typing all 20 from scratch.
                                setFormData({
                                  ...formData,
                                  letter_of_engagement_terms: [
                                    "{company_name} is engaged in providing comprehensive business process management services. Based on your application, we are pleased to confirm your registration for potential Work Assignments as 'Customer'.",
                                    "You shall perform all assigned duties faithfully, diligently, and to the best of your abilities as directed by the Management on a full-time basis. You shall not engage in any other trade, business, or employment during working hours.",
                                    "Registration with {company_name} does not guarantee any Work Assignment and remains subject to the selection process and business requirements.",
                                    "The terms contained in this Letter of Engagement and specific Work Assignment Letters shall govern the employment relationship.",
                                    "Remuneration for services rendered shall be paid by {company_name} as specified in the Work Assignment Letter, subject to applicable statutory deductions.",
                                    "All intellectual property rights arising from work performed shall vest exclusively with the Customer. You agree to assign all such rights without additional compensation.",
                                    "All proprietary and confidential information of the Customer shall be treated with utmost confidentiality. You shall not disclose such information to any third party.",
                                    "This Letter of Engagement does not create any employment obligation until you are formally selected for a specific Work Assignment. This letter does not confer employee status.",
                                    "Upon formal selection, the relationship shall be Employment/Contract of Service for the duration specified in the Work Assignment Letter.",
                                    "Termination shall not affect obligations that have accrued prior to termination. Confidentiality and IP obligations shall survive termination.",
                                    "You shall be entitled to statutory benefits under PF Act, ESI Act, Bonus Act, and Gratuity Act upon meeting the eligibility criteria as per applicable regulations.",
                                    "The Company maintains a formal Internal Complaints Committee (ICC) for prevention of sexual harassment. Any complaints should be reported to the ICC.",
                                    "You shall not maintain any direct or indirect association with other business entities during the tenure of your employment without prior written approval.",
                                    "The Company has a strict policy regarding relatives in employment. Any concealment of such information shall be grounds for termination.",
                                    "Your deployment at client locations is contingent upon successful completion of mandatory training programs. Failure may result in discontinuation.",
                                    "The Company does not charge any recruitment fees. You declare that no payment has been made to any person for securing this employment.",
                                    "Your services may be transferred within India or to client entities as per business requirements.",
                                    "You are required to disclose any pending legal proceedings. False declaration shall be grounds for immediate termination.",
                                    "This offer does not confer employee status until completion of joining formalities. Please contact HR at 07579997999 and complete formalities within {joining_days} working days.",
                                    "This engagement is subject to additional terms and conditions as may be communicated by the Management from time to time.",
                                  ],
                                });
                              }}
                            >
                              Load default 20 clauses so I can edit them
                            </Button>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="email" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <Label className="text-base font-semibold">Email Template (optional)</Label>
                            <p className="text-xs text-slate-500 mt-1">
                              Override the default email subject &amp; body sent with this offer letter.
                              Leave both blank to use the system default.
                            </p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={emailPreview.loading || (!formData.email_subject && !formData.email_body)}
                              data-testid="email-preview-btn"
                              onClick={async () => {
                                if (!editingTemplate?.id) {
                                  toast.error('Save the template first to preview the email.');
                                  return;
                                }
                                try {
                                  setEmailPreview((p) => ({ ...p, loading: true }));
                                  const res = await previewOfferEmailTemplate(editingTemplate.id, {
                                    email_subject: formData.email_subject || null,
                                    email_body: formData.email_body || null,
                                  });
                                  setEmailPreview({
                                    subject: res.data.subject || '',
                                    body: res.data.body || '',
                                    loading: false,
                                  });
                                } catch (err) {
                                  setEmailPreview((p) => ({ ...p, loading: false }));
                                  toast.error(err.response?.data?.detail || 'Failed to preview email');
                                }
                              }}
                            >
                              {emailPreview.loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
                              Preview
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={!editingTemplate?.id}
                              data-testid="email-history-btn"
                              onClick={() => {
                                if (!editingTemplate?.id) {
                                  toast.error('Save the template first to view version history.');
                                  return;
                                }
                                setVersionDialogOpen(true);
                              }}
                              title="View email version history & acceptance rates"
                            >
                              <History size={14} className="mr-1" />
                              History
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              disabled={sendingTestEmail || (!formData.email_subject && !formData.email_body)}
                              data-testid="email-send-test-btn"
                              onClick={async () => {
                                if (!editingTemplate?.id) {
                                  toast.error('Save the template first to send a test email.');
                                  return;
                                }
                                const target = window.prompt(
                                  'Send test email to (using dummy candidate data):',
                                  testEmailAddress || user?.email || ''
                                );
                                if (!target) return;
                                const trimmed = target.trim();
                                if (!trimmed.includes('@')) {
                                  toast.error('Please enter a valid email address.');
                                  return;
                                }
                                setTestEmailAddress(trimmed);
                                try {
                                  setSendingTestEmail(true);
                                  const res = await sendTestOfferEmail(editingTemplate.id, {
                                    email_subject: formData.email_subject || null,
                                    email_body: formData.email_body || null,
                                    to_email: trimmed,
                                  });
                                  toast.success(`Test email sent to ${res.data.sent_to}`);
                                } catch (err) {
                                  toast.error(err.response?.data?.detail || 'Failed to send test email');
                                } finally {
                                  setSendingTestEmail(false);
                                }
                              }}
                            >
                              {sendingTestEmail ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Mail size={14} className="mr-1" />}
                              Send Test
                            </Button>
                          </div>
                        </div>

                        <div className="rounded-md border bg-amber-50 border-amber-200 p-3 text-xs text-amber-800">
                          <div className="font-semibold mb-1 flex items-center gap-1">
                            <Mail size={12} /> Available placeholders (click to insert into the focused field)
                          </div>
                          <div className="flex flex-wrap gap-1.5">
                            {['candidate_name','honorific','designation','work_location','company_name','offer_id','employee_id','offer_date','start_date','total_ctc','gross_salary','net_salary'].map((p) => (
                              <button
                                key={p}
                                type="button"
                                className="px-2 py-0.5 rounded-full bg-white border border-amber-300 text-amber-900 hover:bg-amber-100 font-mono"
                                data-testid={`placeholder-${p}`}
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                  const token = '{' + p + '}';
                                  const targetField = lastFocusedField === 'subject' ? 'email_subject' : 'email_body';
                                  setFormData((prev) => ({
                                    ...prev,
                                    [targetField]: (prev[targetField] || '') + token,
                                  }));
                                }}
                              >
                                {'{' + p + '}'}
                              </button>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email_subject">Subject Line</Label>
                          <Input
                            id="email_subject"
                            value={formData.email_subject || ''}
                            onChange={(e) => setFormData({ ...formData, email_subject: e.target.value })}
                            onFocus={() => setLastFocusedField('subject')}
                            placeholder="e.g., Welcome {candidate_name} — your offer at {company_name}"
                            data-testid="email-subject-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="email_body">Body (HTML allowed)</Label>
                          <Textarea
                            id="email_body"
                            value={formData.email_body || ''}
                            onChange={(e) => setFormData({ ...formData, email_body: e.target.value })}
                            onFocus={() => setLastFocusedField('body')}
                            placeholder="<p>Dear {honorific} {candidate_name},</p><p>We are pleased to offer you the role of <strong>{designation}</strong>…</p>"
                            rows={10}
                            className="font-mono text-xs"
                            data-testid="email-body-input"
                          />
                          <p className="text-xs text-slate-500">
                            Tip: Click a placeholder above to insert it at the end of the last-focused field (subject or body).
                          </p>
                        </div>

                        {(emailPreview.subject || emailPreview.body) && (
                          <div className="border rounded-md overflow-hidden" data-testid="email-preview-pane">
                            <div className="bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 flex items-center justify-between">
                              <span>📧 Preview (with sample data)</span>
                              <button
                                type="button"
                                className="text-slate-500 hover:text-slate-700"
                                onClick={() => setEmailPreview({ subject: '', body: '', loading: false })}
                                aria-label="Close preview"
                              >
                                <X size={14} />
                              </button>
                            </div>
                            <div className="p-3 space-y-2 bg-white">
                              <div className="text-xs">
                                <span className="text-slate-500">Subject: </span>
                                <span className="font-semibold text-slate-800" data-testid="preview-subject">{emailPreview.subject}</span>
                              </div>
                              <div
                                className="text-sm border-t pt-2 prose prose-sm max-w-none"
                                data-testid="preview-body"
                                dangerouslySetInnerHTML={{ __html: emailPreview.body }}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </TabsContent>

                    <TabsContent value="esign" className="space-y-4 mt-4">
                      <div className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <Label className="text-base font-semibold">Signature placement (Zoho Sign)</Label>
                            <p className="text-xs text-slate-500 mt-1">
                              Choose where the signature & date fields should be anchored on the annexure PDF.
                              Click any cell of the page below to set the anchor. Default is bottom-right.
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant={esignPreviewImage ? 'secondary' : 'outline'}
                            size="sm"
                            disabled={esignPreviewLoading}
                            data-testid="esign-preview-real-pdf-btn"
                            onClick={async () => {
                              if (!editingTemplate?.id) {
                                toast.error('Save the template first to preview on a real PDF.');
                                return;
                              }
                              if (esignPreviewImage) {
                                setEsignPreviewImage(null);
                                setEsignPreviewMeta({ pageCount: 0, pageIndex: 0, annexureFilename: '' });
                                return;
                              }
                              try {
                                setEsignPreviewLoading(true);
                                // Fetch meta first to know how many pages exist
                                const meta = await api.get(`/offer-letters/templates/${editingTemplate.id}/esign-preview-meta`);
                                const pageCount = meta.data.page_count || 1;
                                const pageParam = formData.esign_signature_position?.page || 'last';
                                const resp = await api.get(
                                  `/offer-letters/templates/${editingTemplate.id}/esign-preview-image?page=${encodeURIComponent(pageParam)}`,
                                  { responseType: 'blob' }
                                );
                                const pageIndex = parseInt(resp.headers['x-page-index'] ?? '0', 10);
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  setEsignPreviewImage(reader.result);
                                  setEsignPreviewMeta({
                                    pageCount,
                                    pageIndex,
                                    annexureFilename: meta.data.annexure_filename || '',
                                  });
                                  setEsignPreviewLoading(false);
                                };
                                reader.readAsDataURL(resp.data);
                              } catch (err) {
                                setEsignPreviewLoading(false);
                                toast.error(err.response?.data?.detail || 'Failed to load preview image. Upload an annexure first.');
                              }
                            }}
                            title="Overlay the placement picker on a real annexure page"
                          >
                            {esignPreviewLoading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Eye size={14} className="mr-1" />}
                            {esignPreviewImage ? 'Hide PDF preview' : 'Preview on real PDF'}
                          </Button>
                        </div>

                        <div className="flex items-start gap-4">
                          {/* Visual A4 picker */}
                          <div
                            className="relative border-2 border-slate-300 rounded shadow-sm overflow-hidden"
                            style={{
                              width: 220,
                              height: 311,
                              flexShrink: 0,
                              backgroundColor: esignPreviewImage ? 'transparent' : '#fff',
                              backgroundImage: esignPreviewImage ? `url(${esignPreviewImage})` : 'none',
                              backgroundSize: 'cover',
                              backgroundPosition: 'top center',
                            }}
                            data-testid="esign-position-picker"
                          >
                            {esignPreviewImage && (
                              <div className="absolute inset-0 bg-white/60 pointer-events-none" />
                            )}
                            <div className="absolute inset-2 grid grid-cols-3 grid-rows-3 gap-1">
                              {['top_left','top_center','top_right','middle_left','middle_center','middle_right','bottom_left','bottom_center','bottom_right'].map((p) => {
                                const pos = formData.esign_signature_position || {};
                                const hasCustomXY = pos.x !== undefined && pos.x !== '' && pos.x !== null;
                                const isAuto = !hasCustomXY && (pos.preset || 'auto') === 'auto';
                                const isSelected = !hasCustomXY && !isAuto && (pos.preset || 'bottom_right') === p;
                                const baseCls = esignPreviewImage
                                  ? (isSelected ? 'bg-indigo-600/90 border-indigo-700 text-white' : 'bg-slate-50/40 border-slate-200/60 hover:bg-indigo-50/80 hover:border-indigo-300 text-transparent hover:text-slate-700')
                                  : (isSelected ? 'bg-indigo-600 border-indigo-700 text-white' : 'bg-slate-50 border-slate-200 hover:bg-indigo-50 hover:border-indigo-300 text-slate-600');
                                return (
                                  <button
                                    key={p}
                                    type="button"
                                    className={`rounded border transition-colors ${baseCls}`}
                                    onClick={() => setFormData({
                                      ...formData,
                                      esign_signature_position: { preset: p, page: (formData.esign_signature_position?.page || 'last') }
                                    })}
                                    title={p.replace('_', ' ')}
                                    data-testid={`esign-pos-${p}`}
                                  >
                                    {isSelected && (
                                      <span className="block text-[10px] font-bold">✎ Sign</span>
                                    )}
                                  </button>
                                );
                              })}
                            </div>
                            {/* Custom X/Y marker (visible only when numeric overrides are set) */}
                            {(() => {
                              const pos = formData.esign_signature_position || {};
                              const x = parseInt(pos.x, 10);
                              const y = parseInt(pos.y, 10);
                              if (Number.isNaN(x) || Number.isNaN(y)) return null;
                              const left = Math.max(2, Math.min(218, (x / 595) * 220));
                              const top = Math.max(2, Math.min(309, (y / 842) * 311));
                              return (
                                <div
                                  className="absolute bg-red-500 text-white text-[9px] font-bold px-1 rounded shadow"
                                  style={{ left, top, transform: 'translate(-50%, -50%)' }}
                                  data-testid="esign-custom-marker"
                                  title={`Custom: x=${x}pt, y=${y}pt`}
                                >
                                  ✎ ({x},{y})
                                </div>
                              );
                            })()}
                            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-white px-1 text-[10px] text-slate-400 font-mono">A4 page (top)</div>
                          </div>

                          <div className="flex-1 space-y-3">
                            <div className="rounded-md bg-indigo-50 border border-indigo-200 p-2.5 space-y-2" data-testid="esign-auto-toggle-row">
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <Label className="text-xs font-semibold text-indigo-900">🪄 Smart auto-detect</Label>
                                  <p className="text-[11px] text-indigo-700 mt-0.5">
                                    Scans the PDF for the largest whitespace gap and places sig+date there automatically.
                                  </p>
                                </div>
                                <Checkbox
                                  id="esign-auto-toggle"
                                  checked={(formData.esign_signature_position?.preset || 'auto') === 'auto'}
                                  onCheckedChange={(checked) => {
                                    if (checked) {
                                      setFormData({
                                        ...formData,
                                        esign_signature_position: {
                                          preset: 'auto',
                                          page: (formData.esign_signature_position?.page || 'last'),
                                          h_align: (formData.esign_signature_position?.h_align || 'right'),
                                        },
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        esign_signature_position: {
                                          preset: 'bottom_right',
                                          page: (formData.esign_signature_position?.page || 'last'),
                                        },
                                      });
                                    }
                                  }}
                                  data-testid="esign-auto-toggle"
                                />
                              </div>
                              {(formData.esign_signature_position?.preset || 'auto') === 'auto' && (
                                <div className="flex items-center gap-2 pt-1">
                                  <span className="text-[11px] text-indigo-700 shrink-0">Horizontal preference:</span>
                                  <div className="inline-flex rounded-md border border-indigo-300 overflow-hidden bg-white" data-testid="esign-h-align-row">
                                    {['left', 'center', 'right'].map((opt) => {
                                      const cur = formData.esign_signature_position?.h_align || 'right';
                                      const isOn = cur === opt;
                                      return (
                                        <button
                                          key={opt}
                                          type="button"
                                          className={`px-2.5 py-0.5 text-[11px] font-medium transition-colors ${isOn ? 'bg-indigo-600 text-white' : 'text-indigo-700 hover:bg-indigo-50'}`}
                                          onClick={() => setFormData({
                                            ...formData,
                                            esign_signature_position: {
                                              ...(formData.esign_signature_position || {}),
                                              h_align: opt,
                                            },
                                          })}
                                          data-testid={`esign-h-align-${opt}`}
                                        >
                                          {opt[0].toUpperCase() + opt.slice(1)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-1.5">
                              <Label className="text-xs">Page anchor</Label>
                              <Select
                                value={formData.esign_signature_position?.page || 'last'}
                                onValueChange={(v) => {
                                  setFormData({
                                    ...formData,
                                    esign_signature_position: { ...(formData.esign_signature_position || {}), page: v }
                                  });
                                  // Invalidate stale page preview if active
                                  if (esignPreviewImage) setEsignPreviewImage(null);
                                }}
                              >
                                <SelectTrigger className="w-full" data-testid="esign-page-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="last">Last page (default)</SelectItem>
                                  <SelectItem value="first">First page</SelectItem>
                                </SelectContent>
                              </Select>
                              <p className="text-[11px] text-slate-500">Where the signature/date fields are placed on the annexure PDF.</p>
                            </div>

                            <div className="flex items-center gap-2 pt-1">
                              <Checkbox
                                id="esign-finetune"
                                checked={!!(formData.esign_signature_position?.x !== undefined && formData.esign_signature_position?.x !== '' && formData.esign_signature_position?.x !== null)}
                                onCheckedChange={(checked) => {
                                  const cur = formData.esign_signature_position || { preset: 'bottom_right', page: 'last' };
                                  if (checked) {
                                    // Seed with current preset's resolved coords so user can nudge from there
                                    const preset = cur.preset || 'bottom_right';
                                    let x = 245, y = 702;
                                    if (preset.endsWith('_left')) x = 100;
                                    else if (preset.endsWith('_center')) x = 222;
                                    if (preset.startsWith('top_')) y = 100;
                                    else if (preset.startsWith('middle_')) y = 401;
                                    setFormData({
                                      ...formData,
                                      esign_signature_position: { ...cur, x, y },
                                    });
                                  } else {
                                    const { x: _x, y: _y, ...rest } = cur;
                                    setFormData({ ...formData, esign_signature_position: rest });
                                  }
                                }}
                                data-testid="esign-finetune-toggle"
                              />
                              <Label htmlFor="esign-finetune" className="text-xs cursor-pointer select-none">
                                Fine-tune (advanced) — nudge by exact pt coordinates
                              </Label>
                            </div>

                            {formData.esign_signature_position?.x !== undefined && formData.esign_signature_position?.x !== '' && formData.esign_signature_position?.x !== null && (
                              <div className="grid grid-cols-2 gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                                <div className="space-y-1">
                                  <Label className="text-[11px] font-medium">X (0–595pt)</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="595"
                                    value={formData.esign_signature_position?.x ?? ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      esign_signature_position: {
                                        ...(formData.esign_signature_position || {}),
                                        x: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                                      }
                                    })}
                                    className="h-8 text-sm"
                                    data-testid="esign-x-input"
                                  />
                                </div>
                                <div className="space-y-1">
                                  <Label className="text-[11px] font-medium">Y (0–842pt)</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="842"
                                    value={formData.esign_signature_position?.y ?? ''}
                                    onChange={(e) => setFormData({
                                      ...formData,
                                      esign_signature_position: {
                                        ...(formData.esign_signature_position || {}),
                                        y: e.target.value === '' ? '' : parseInt(e.target.value, 10),
                                      }
                                    })}
                                    className="h-8 text-sm"
                                    data-testid="esign-y-input"
                                  />
                                </div>
                                <p className="col-span-2 text-[10px] text-amber-700">
                                  Top-left origin. Custom coords override the 3×3 preset above.
                                </p>
                              </div>
                            )}

                            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700 space-y-1">
                              <div className="font-semibold flex items-center gap-1">
                                <Eye size={12} /> Current setting
                              </div>
                              {(formData.esign_signature_position?.x !== undefined && formData.esign_signature_position?.x !== '' && formData.esign_signature_position?.x !== null) ? (
                                <div>
                                  Position: <span className="font-mono text-red-600">custom ({formData.esign_signature_position.x}pt, {formData.esign_signature_position.y}pt)</span>
                                </div>
                              ) : (formData.esign_signature_position?.preset || 'auto') === 'auto' ? (
                                <div>
                                  Position: <span className="font-mono text-emerald-700">🪄 auto (smart whitespace, {formData.esign_signature_position?.h_align || 'right'})</span>
                                </div>
                              ) : (
                                <div>
                                  Position: <span className="font-mono text-indigo-700">{(formData.esign_signature_position?.preset || 'bottom_right').replace('_', ' ')}</span>
                                </div>
                              )}
                              <div>
                                Page: <span className="font-mono text-indigo-700">{formData.esign_signature_position?.page || 'last'}</span>
                              </div>
                              <div className="text-[11px] text-slate-500 pt-1">
                                Signature box: 150×40pt &nbsp;•&nbsp; Date field: 150×30pt (50pt above sig)
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Page slider — only when PDF preview is active and annexure has >1 page */}
                        {esignPreviewImage && esignPreviewMeta.pageCount > 1 && (
                          <div
                            className="flex items-center gap-3 p-2 bg-slate-50 border border-slate-200 rounded-md"
                            data-testid="esign-page-slider-row"
                          >
                            <span className="text-xs font-medium text-slate-700 shrink-0">Preview page:</span>
                            <input
                              type="range"
                              min={1}
                              max={esignPreviewMeta.pageCount}
                              value={esignPreviewMeta.pageIndex + 1}
                              data-testid="esign-page-slider"
                              className="flex-1 accent-indigo-600"
                              onChange={async (e) => {
                                const target = parseInt(e.target.value, 10);
                                if (!editingTemplate?.id) return;
                                try {
                                  setEsignPreviewLoading(true);
                                  const resp = await api.get(
                                    `/offer-letters/templates/${editingTemplate.id}/esign-preview-image?page=${target}`,
                                    { responseType: 'blob' }
                                  );
                                  const pageIndex = parseInt(resp.headers['x-page-index'] ?? '0', 10);
                                  const reader = new FileReader();
                                  reader.onloadend = () => {
                                    setEsignPreviewImage(reader.result);
                                    setEsignPreviewMeta((m) => ({ ...m, pageIndex }));
                                    setEsignPreviewLoading(false);
                                  };
                                  reader.readAsDataURL(resp.data);
                                } catch (err) {
                                  setEsignPreviewLoading(false);
                                  toast.error('Failed to load page preview');
                                }
                              }}
                            />
                            <span className="text-xs text-slate-600 shrink-0 font-mono" data-testid="esign-page-slider-label">
                              {esignPreviewMeta.pageIndex + 1} / {esignPreviewMeta.pageCount}
                              {esignPreviewLoading && <Loader2 size={11} className="inline ml-1 animate-spin" />}
                            </span>
                          </div>
                        )}
                      </div>
                    </TabsContent>
                  </Tabs>

                  <DialogFooter>
                    <Button type="button" variant="outline" onClick={() => {
                      setDialogOpen(false);
                      resetForm();
                    }}>
                      Cancel
                    </Button>
                    <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700">
                      {editingTemplate ? 'Update Template' : 'Create Template'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-indigo-100 text-xs">Total Templates</p>
                  <p className="text-2xl font-bold">{templates.length}</p>
                </div>
                <FileText className="h-8 w-8 text-indigo-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs">Job Level</p>
                  <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'job_level').length}</p>
                </div>
                <Briefcase className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs">Employment Type</p>
                  <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'employment_type').length}</p>
                </div>
                <Users className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs">Custom</p>
                  <p className="text-2xl font-bold">{templates.filter(t => t.template_type === 'custom').length}</p>
                </div>
                <Settings2 className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Templates List */}
        <Card>
          <CardHeader>
            <CardTitle>All Templates</CardTitle>
            <CardDescription>Configure templates for different types of offer letters</CardDescription>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-12">
                <FileText size={48} className="mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No templates found</p>
                <Button 
                  className="mt-4"
                  variant="outline"
                  onClick={handleSeedDefaults}
                >
                  Load Default Templates
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => {
                  const TypeIcon = templateTypeOptions.find(t => t.value === template.template_type)?.icon || Settings2;
                  return (
                    <Card key={template.id} className={`border-2 ${template.is_default ? 'border-indigo-300 bg-indigo-50/50' : 'border-slate-100'}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`p-2 rounded-lg ${templateTypeColors[template.template_type]}`}>
                              <TypeIcon size={16} />
                            </div>
                            <div>
                              <h3 className="font-medium text-slate-900 flex items-center gap-1">
                                {template.name}
                                {template.is_default && <Star size={14} className="text-yellow-500 fill-yellow-500" />}
                              </h3>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${templateTypeColors[template.template_type]}`}>
                                {(template.template_type || template.category || '').replace('_', ' ')}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {template.description && (
                          <p className="text-sm text-slate-500 mb-3 line-clamp-2">{template.description}</p>
                        )}
                        
                        <div className="text-xs text-slate-500 space-y-1 mb-3">
                          <p><strong>Probation:</strong> {template.terms_and_conditions?.probation_period || '6 months'}</p>
                          <p><strong>Notice:</strong> {template.terms_and_conditions?.notice_period || '30 days'}</p>
                          {template.category && <p><strong>Category:</strong> {template.category}</p>}
                        </div>
                        
                        <div className="flex flex-wrap justify-end gap-1 pt-2 border-t border-slate-100">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={() => { setClauseEditTarget(template); setClauseEditorOpen(true); }}
                            data-testid={`edit-clauses-btn-${template.id}`}
                          >
                            <ClipboardList size={14} className="mr-1" />
                            Clauses
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-indigo-700 hover:text-indigo-900 hover:bg-indigo-50"
                            onClick={() => handleLegalPreview(template)}
                            disabled={legalPreviewingId === template.id}
                            title="One-click preview of the 8 standard legal clauses for this template"
                            data-testid={`legal-preview-btn-${template.id}`}
                          >
                            {legalPreviewingId === template.id ? (
                              <Loader2 size={14} className="mr-1 animate-spin" />
                            ) : (
                              <Scale size={14} className="mr-1" />
                            )}
                            Legal
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={() => { setAnnexuresTarget(template); setAnnexuresOpen(true); }}
                            data-testid={`manage-annexures-btn-${template.id}`}
                          >
                            <Paperclip size={14} className="mr-1" />
                            Annexures
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs"
                            onClick={() => handleEdit(template)}
                          >
                            <Edit2 size={14} className="mr-1" />
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-xs text-indigo-700 hover:text-indigo-900 hover:bg-indigo-50"
                            onClick={() => { setDuplicateTarget(template); setDuplicateDialogOpen(true); }}
                            title="Create a full copy of this template (including all PDF terms, clauses, and annexures)"
                            data-testid={`duplicate-template-btn-${template.id}`}
                          >
                            <Copy size={14} className="mr-1" />
                            Duplicate
                          </Button>
                          {!template.is_default && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs text-red-600 hover:text-red-700"
                              onClick={() => handleDelete(template.id)}
                            >
                              <Trash2 size={14} className="mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <TemplateClauseEditor
        template={clauseEditTarget}
        open={clauseEditorOpen}
        onOpenChange={setClauseEditorOpen}
        onSaved={fetchTemplates}
      />
      <TemplateAnnexuresManager
        template={annexuresTarget}
        open={annexuresOpen}
        onOpenChange={setAnnexuresOpen}
        onChanged={fetchTemplates}
      />
      <EmailVersionHistoryDialog
        open={versionDialogOpen}
        onOpenChange={setVersionDialogOpen}
        templateId={editingTemplate?.id}
        templateName={editingTemplate?.name}
        onRestore={({ email_subject, email_body }) => {
          setFormData((prev) => ({ ...prev, email_subject: email_subject || '', email_body: email_body || '' }));
        }}
      />
      <AttachHgipBulkDialog
        key={hgipDialogOpen ? 'open' : 'closed'}
        open={hgipDialogOpen}
        onOpenChange={(open) => {
          setHgipDialogOpen(open);
          if (!open) fetchTemplates();  // refresh annexure counts on close
        }}
      />
      <AnnexureVersionHistoryDialog
        open={annexureHistoryOpen}
        onOpenChange={setAnnexureHistoryOpen}
        currentUserRole={user?.role || 'admin'}
      />
      <NoticePeriodAuditDialog
        open={noticeAuditOpen}
        onOpenChange={setNoticeAuditOpen}
      />
      <NoticeContradictionAuditDialog
        open={contradictionAuditOpen}
        onOpenChange={setContradictionAuditOpen}
        onCleanupDone={fetchTemplates}
      />
      <DuplicateTemplateDialog
        template={duplicateTarget}
        open={duplicateDialogOpen}
        onOpenChange={(open) => {
          setDuplicateDialogOpen(open);
          if (!open) setDuplicateTarget(null);
        }}
        onDuplicated={() => fetchTemplates()}
      />
    </Layout>
  );
};

export default OfferLetterTemplates;
